import { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Bell, Trash } from 'lucide-react';
import { format } from 'date-fns';

export default function ReminderDialog({ noteId, onClose }) {
  const { reminders, createReminder, deleteReminder, completeReminder } = useApp();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');

  const noteReminders = reminders.filter((r) => r.note_id === noteId);

  const handleCreate = async () => {
    if (!date) return;
    const remindAt = new Date(`${date}T${time}`).toISOString();
    await createReminder({ note_id: noteId, remind_at: remindAt, title: title || 'Note reminder' });
    setTitle('');
    setDate('');
    setTime('09:00');
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle>Reminders</DialogTitle>
          <DialogDescription>Set reminders for this note.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Reminder title (optional)" className="h-8 text-sm" data-testid="reminder-title-input" />
            <div className="flex gap-2">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="flex-1 h-8 text-sm" data-testid="reminder-date-input" />
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-28 h-8 text-sm" data-testid="reminder-time-input" />
            </div>
            <Button onClick={handleCreate} size="sm" className="w-full h-8" disabled={!date} data-testid="reminder-create-btn">
              <Bell className="w-3.5 h-3.5 mr-1" /> Set Reminder
            </Button>
          </div>
          {noteReminders.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Active Reminders</p>
              {noteReminders.map((r) => (
                <div key={r.id} className="flex items-center gap-2 py-1.5 px-2 rounded-sm bg-secondary/30" data-testid={`reminder-item-${r.id}`}>
                  <Bell className={`w-3.5 h-3.5 ${r.is_completed ? 'text-green-400' : 'text-accent-amber'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{r.title}</p>
                    <p className="text-xs text-muted-foreground">{format(new Date(r.remind_at), 'PPp')}</p>
                  </div>
                  {!r.is_completed && (
                    <button onClick={() => completeReminder(r.id)} className="text-xs text-muted-foreground hover:text-green-400">Done</button>
                  )}
                  <button onClick={() => deleteReminder(r.id)} className="p-1 text-muted-foreground hover:text-red-400">
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
